import type { Locale } from '../i18n';
import type { StageConversion } from '../types/api';

/** Number with locale-aware grouping/fraction (1 234,5 in ru). */
export function formatNumber(value: number, locale: Locale, maxFractionDigits = 0): string {
  return new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
    maximumFractionDigits: maxFractionDigits,
  }).format(value);
}

/**
 * Money, short board form: 292 000 000 → "292 млн ₽" (ru) / "292 M ₽" (en);
 * 850 000 → "850 000 ₽". Zero renders as "0 ₽" (not "0 млн").
 */
export function formatMoneyShort(value: number, locale: Locale, millionLabel: string): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    const millions = value / 1_000_000;
    // One decimal, but drop it when it rounds to .0 (292,0 → 292).
    const rounded = Math.round(millions * 10) / 10;
    return `${formatNumber(rounded, locale, rounded % 1 === 0 ? 0 : 1)} ${millionLabel}`;
  }
  return `${formatNumber(Math.round(value), locale)} ₽`;
}

/**
 * Funnel conversion % for the KPI strip: chained (multiplied) backend
 * conversion[] rates across the stage ladder, excluding the TKP pair — its
 * "advanced" is always 0 by design (WIN is a separate event type), so
 * including it would zero out the whole chain.
 */
export function funnelConversionPct(conversion: StageConversion[]): number {
  const pairs = conversion.filter((c) => c.from !== 'TKP');
  if (pairs.length === 0) return 0;
  const rate = pairs.reduce((acc, c) => acc * c.rate, 1);
  return Math.round(rate * 100);
}
